"use client";

import Image from "next/image";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

// ── Data ────────────────────────────────────────────────────────────────────
// Real, verified coverage only. Every URL below was checked and mentions
// JustGold; do not add outlets we have not actually been featured in.

const publications = [
  { name: "Gulf News",               src: "/logo-gulf-news.svg",               width: 160, height: 40 },
  { name: "Khaleej Times",           src: "/logo-khaleej-times.svg",           width: 180, height: 44 },
  { name: "Dubai Fintech Summit",    src: "/logo-dubai-fintech-summit.svg",    width: 180, height: 44 },
  { name: "Technology Integrator",   src: "/logo-technology-integrator.svg",   width: 200, height: 50 },
];

function getPublicationLogo(name: string) {
  return publications.find((p) => p.name === name) ?? publications[0];
}

const articles = [
  {
    publication: "Gulf News",
    date: "February 2026",
    headline:
      "JustGold App introduces silver savings, enhancing precious metals access",
    excerpt:
      "JustGold expands beyond gold to offer silver, giving customers regulated, Shariah compliant access to physically backed precious metals in one app.",
    url: "https://gulfnews.com/business/corporate-news/justgold-app-introduces-silver-savings-enhancing-precious-metals-access-1.500446028",
  },
  {
    publication: "Gulf News",
    date: "December 2025",
    headline: "The Just Gold App achieves Shariah certification",
    excerpt:
      "Following an independent review by Panacea Consulting, Bahrain, JustGold receives formal certification that every purchase is real physical gold handled under recognised Shariah principles.",
    url: "https://gulfnews.com/business/corporate-news/the-just-gold-app-achieves-shariah-certification-1.500380335",
  },
  {
    publication: "Khaleej Times",
    date: "November 2025",
    headline:
      "The calm after the digital gold rush: Why trust matters more than technology",
    excerpt:
      "As the UAE digital gold market matures, JustGold is highlighted for vaulted custody, independent trustee oversight, and gold ownership starting from AED 10.",
    url: "https://www.khaleejtimes.com/business/the-calm-after-the-digital-gold-rush-why-trust-matters-more-than-technology",
  },
  {
    publication: "Khaleej Times",
    date: "October 2025",
    headline: "Diwali Gold, reimagined for a new generation",
    excerpt:
      "JustGold features as the modern way to gift gold for Diwali: 24K, 999.9 purity at official UAE rates, vaulted and insured, with instant sell or physical delivery.",
    url: "https://www.khaleejtimes.com/supplements/diwali-gold-reimagined-for-a-new-generation",
  },
];

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Marquee ─────────────────────────────────────────────────────────────────

function Marquee() {
  // Duplicate the list so the -50% translate keyframe loops seamlessly.
  const items = [...publications, ...publications];

  return (
    <div className="relative w-full overflow-hidden py-8">
      {/* Fade edges */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-white to-transparent" />

      <div className="flex w-max animate-marquee items-center gap-20">
        {items.map((pub, i) => (
          <div key={`${pub.name}-${i}`} className="flex shrink-0 items-center opacity-60 grayscale select-none">
            <Image
              src={pub.src}
              alt={pub.name}
              width={pub.width}
              height={pub.height}
              className="h-8 w-auto object-contain"
              draggable={false}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Article Card ────────────────────────────────────────────────────────────

function ArticleCard({
  article,
  reduce,
}: {
  article: (typeof articles)[number];
  reduce: boolean;
}) {
  const logo = getPublicationLogo(article.publication);

  return (
    <motion.a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      variants={fadeUp(reduce)}
      className={cn(
        "group flex shrink-0 snap-start flex-col gap-4 rounded-card border border-[#e3ecfb] bg-white p-6",
        "w-[82vw] max-w-[320px] sm:w-auto sm:max-w-none",
        "transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(14,58,138,0.08)]"
      )}
    >
      {/* Publication logo */}
      <Image
        src={logo.src}
        alt={article.publication}
        width={logo.width}
        height={logo.height}
        className="h-6 w-auto object-contain object-left"
      />

      {/* Headline */}
      <h3 className="text-[17px] font-bold leading-[1.25] tracking-[-0.01em] text-text-heading">
        {article.headline}
      </h3>

      {/* Excerpt */}
      <p className="text-sm leading-[1.55] text-text-body">
        {article.excerpt}
      </p>

      {/* Date */}
      <span className="text-xs text-primary-300">{article.date}</span>

      {/* Read more */}
      <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary-500 transition-colors group-hover:text-primary-600">
        Read more
        <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.2} />
      </span>
    </motion.a>
  );
}

// ── Section ─────────────────────────────────────────────────────────────────

export function MediaCoverage() {
  const reduce = useReducedMotion() ?? false;

  return (
    <motion.section
      className="relative overflow-hidden bg-white"
      aria-labelledby="media-coverage-heading"
      variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } } }}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
    >
      <div className="relative mx-auto max-w-[1440px] px-6 pb-16 sm:px-10 sm:pb-20 lg:px-20 lg:pb-24">
        {/* Header */}
        <motion.div
          variants={fadeUp(reduce)}
          className="mb-6 max-w-[720px]"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-primary-500">
            Media coverage
          </p>
          <h2
            id="media-coverage-heading"
            className="scroll-mt-28 text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
          >
            What the press has said
          </h2>
          <p className="mt-4 max-w-[560px] text-base leading-[1.55] text-text-body">
            The UAE&apos;s leading publications have covered JustGold for
            making gold investment accessible, transparent, and trustworthy.
          </p>
        </motion.div>

        {/* Marquee */}
        <motion.div variants={fadeUp(reduce)}>
          <Marquee />
        </motion.div>

        {/* Article cards — swipeable carousel on mobile, grid from sm up */}
        <motion.div
          variants={container}
          className="mt-4 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:snap-none sm:gap-6 sm:overflow-visible sm:pb-0 sm:grid-cols-2 lg:grid-cols-4"
        >
          {articles.map((article) => (
            <ArticleCard
              key={article.headline}
              article={article}
              reduce={reduce}
            />
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}
